// Threshold — converts the image to pure black and white based on a luminance
// threshold. Pixels above the threshold become white, below become black.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface ThresholdParams {
  /** 0..255 luminance threshold. Default 128. */
  level: number;
}

export const Threshold: Filter<ThresholdParams> = {
  id: "threshold",
  displayName: "Threshold",
  defaultParams: { level: 128 },

  apply(input: PixelBuffer, params: ThresholdParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const level = clampByte(params.level);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const lum = (r * 77 + g * 150 + b * 29) >> 8; // Rec.601 fixed-point
      const val = lum <= level ? 0 : 255;
      dst[o] = val;
      dst[o + 1] = val;
      dst[o + 2] = val;
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};

function clampByte(v: number): number {
  if (!isFinite(v)) return 128;
  if (v < 0) return 0;
  if (v > 255) return 255;
  return Math.round(v);
}
